<template>
  <div class="h-full flex flex-col bg-white dark:bg-gray-800">
    <!-- Header -->
    <div class="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
      <div class="flex items-center justify-between">
        <!-- Back Button -->
        <button
          class="inline-flex items-center px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors duration-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
          @click="$emit('backClicked')">
          <ArrowRight class="h-4 w-4 mr-2" :class="[$store.state.portfolio.ui_options?.language == 'ar' ? 'rotate-180' : 'rotate-180']" />
          {{ $t("Back to list") }}
        </button>

        <!-- Action Buttons -->
        <div class="flex items-center space-x-2">
          <button
            :class="[
              selectedEmail?.meta_data.starred
                ? 'text-yellow-500 bg-yellow-50 dark:bg-yellow-900/20'
                : 'text-gray-400 hover:text-yellow-500 hover:bg-yellow-50 dark:hover:bg-yellow-900/20'
            ]"
            class="p-2 rounded-lg transition-colors duration-200"
            :title="selectedEmail?.meta_data.starred ? $t('Unstar') : $t('Star')"
            @click.prevent="$emit('clicked', selectedEmail, 'starred')">
            <Star :class="[selectedEmail?.meta_data.starred ? 'fill-current' : '']" class="h-5 w-5" />
          </button>

          <button
            class="p-2 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors duration-200"
            :title="$t('Reply')"
            @click.prevent="$emit('clicked', selectedEmail, 'reply')">
            <Reply class="h-5 w-5" />
          </button>

          <button
            v-if="selectedEmail?.meta_data?.to?.length > 1 || selectedEmail?.meta_data?.cc?.length"
            class="p-2 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors duration-200"
            :title="$t('Reply to all')"
            @click.prevent="$emit('clicked', selectedEmail, 'replyall')">
            <ReplyAll class="h-5 w-5" />
          </button>

          <button
            class="p-2 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors duration-200"
            :title="$t('Forward')"
            @click.prevent="$emit('clicked', selectedEmail, 'forward')">
            <Forward class="h-5 w-5" />
          </button>

          <div class="h-6 w-px bg-gray-200 dark:bg-gray-600" />

          <button
            v-if="$route?.query?.tab !== 'trash'"
            class="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-200"
            :title="$t('Delete')"
            @click.prevent="$emit('clicked', selectedEmail, 'trash')">
            <Trash2 class="h-5 w-5" />
          </button>

          <button
            v-else
            class="p-2 rounded-lg text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors duration-200"
            :title="$t('Restore')"
            @click.prevent="$emit('clicked', selectedEmail, 'restore')">
            <RefreshCcw class="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>

    <!-- Email Content -->
    <div class="flex-1 overflow-y-auto">
      <!-- Email Header Info -->
      <div class="px-6 py-6 border-b border-gray-100 dark:border-gray-700">
        <!-- Subject -->
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white mb-4">
          {{ selectedEmail?.name || $t("(No Subject)") }}
        </h1>

        <!-- Sender Info -->
        <div class="flex items-start justify-between">
          <div class="flex items-center space-x-4">
            <div class="relative">
              <Avatar
                :radius="48"
                :first-name="
                  selectedEmail?.meta_data?.from_[0]?.name
                    ? selectedEmail?.meta_data?.from_[0]?.name
                    : selectedEmail?.meta_data?.from_[0].email
                "
                :last-name="selectedEmail?.meta_data?.from_[0]?.name ? selectedEmail?.meta_data?.from_[0].name.split(' ')[1] : ''" />

              <!-- Platform Badge -->
              <div
                class="absolute -bottom-1 -right-1 w-6 h-6 bg-white dark:bg-gray-800 rounded-full flex items-center justify-center border border-gray-200 dark:border-gray-600">
                <GoogleIcon v-if="selectedEmail?.origin == 'gmail'" class="w-3 h-3" />
                <MicrosoftIcon v-else-if="selectedEmail?.origin == 'outlook'" class="w-3 h-3" />
                <svg v-else class="w-3 h-3 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
              </div>
            </div>

            <div class="flex-1 min-w-0">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
                {{ selectedEmail?.meta_data?.from_[0]?.name || $t("(No Name)") }}
              </h3>
              <p class="text-sm text-gray-600 dark:text-gray-400">
                {{ selectedEmail?.meta_data?.from_[0].email }}
              </p>
            </div>
          </div>

          <!-- Date and Time -->
          <div class="text-right">
            <p class="text-sm text-gray-600 dark:text-gray-400">
              {{ $moment.utc(selectedEmail?.interaction_date).local().format("MMM DD, YYYY") }}
            </p>
            <p class="text-xs text-gray-500 dark:text-gray-500">
              {{ $moment.utc(selectedEmail?.interaction_date).local().format("hh:mm A") }}
            </p>
          </div>
        </div>

        <!-- Recipients -->
        <div class="mt-4 space-y-2">
          <div v-if="selectedEmail?.meta_data?.to?.length" class="flex items-start text-sm">
            <span class="font-medium text-gray-700 dark:text-gray-300 mr-2 min-w-0">{{ $t("To") }}:</span>
            <div class="flex flex-wrap gap-1">
              <span
                v-for="(recipient, index) in selectedEmail.meta_data.to"
                :key="index"
                class="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                {{ recipient.name || recipient.email }}
              </span>
            </div>
          </div>

          <div v-if="selectedEmail?.meta_data?.cc?.length" class="flex items-start text-sm">
            <span class="font-medium text-gray-700 dark:text-gray-300 mr-2 min-w-0">{{ $t("CC") }}:</span>
            <div class="flex flex-wrap gap-1">
              <span
                v-for="(recipient, index) in selectedEmail.meta_data.cc"
                :key="index"
                class="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                {{ recipient.name || recipient.email }}
              </span>
            </div>
          </div>

          <div v-if="selectedEmail?.meta_data?.bcc?.length" class="flex items-start text-sm">
            <span class="font-medium text-gray-700 dark:text-gray-300 mr-2 min-w-0">{{ $t("BCC") }}:</span>
            <div class="flex flex-wrap gap-1">
              <span
                v-for="(recipient, index) in selectedEmail.meta_data.bcc"
                :key="index"
                class="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                {{ recipient.name || recipient.email }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Email Body -->
      <div class="px-6 py-6">
        <div class="prose prose-blue dark:prose-invert max-w-none">
          <SafeHtml :html="selectedEmail?.description" />
        </div>
      </div>

      <!-- Thread Emails -->
      <div v-if="selectedEmail?.children?.length" class="px-6 pb-6">
        <div class="border-t border-gray-200 dark:border-gray-700 pt-6">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {{ $t("Thread") }} ({{ selectedEmail.children.length + 1 }} {{ $t("messages") }})
          </h3>

          <div class="space-y-4">
            <div v-for="(child_email, index) in selectedEmail?.children" :key="child_email.id">
              <ChildInboxEmail
                v-if="shouldShowChildEmail(index)"
                :initially-expanded="selectedEmail?.children?.length - 1 == index"
                :email="selectedEmail?.children[selectedEmail?.children?.length - 1 - index]" />
              <ExpandChildEmailsButton
                v-if="!expand_all_child_emails && selectedEmail?.children?.length - 2 > 0 && index == 1"
                :collapsed-emails="selectedEmail?.children?.length - 2"
                @click="expand_all_child_emails = true" />
            </div>
          </div>
        </div>
      </div>

      <!-- Attachments -->
      <div v-if="attachments?.length" class="px-6 pb-6">
        <div class="border-t border-gray-200 dark:border-gray-700 pt-6">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {{ $t("Attachments") }} ({{ attachments.length }})
          </h3>
          <AttachmentsField v-model="attachments" :disabled="true" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Avatar from "@/components/shared/Avatar.vue"
import ChildInboxEmail from "@/components/shared/ChildInboxEmail.vue"
import ExpandChildEmailsButton from "@/components/shared/ExpandChildEmailsButton.vue"
import { Star, Reply, Forward, Trash2, ArrowRight, RefreshCcw, ReplyAll } from "lucide-vue-next"
import GoogleIcon from "@/components/shared/icons/GoogleIcon.vue"
import MicrosoftIcon from "@/components/shared/icons/MicrosoftIcon.vue"
import SafeHtml from "@/components/shared/SafeHtml.vue"
import AttachmentsField from "@/components/shared/form-inputs/AttachmentsField.vue"

export default {
  components: {
    Star,
    ArrowRight,
    RefreshCcw,
    Avatar,
    Reply,
    Forward,
    Trash2,
    ReplyAll,
    ChildInboxEmail,
    ExpandChildEmailsButton,
    GoogleIcon,
    MicrosoftIcon,
    SafeHtml,
    AttachmentsField
  },
  props: {
    selectedEmail: {
      type: Object,
      required: false
    }
  },
  emits: ["backClicked", "clicked"],
  data() {
    return {
      expand_all_child_emails: false,
      attachments: null
    }
  },

  watch: {
    selectedEmail() {
      this.attachments = this.selectedEmail?.attachments
    }
  },

  created() {},

  methods: {
    shouldShowChildEmail(index) {
      if (this.selectedEmail?.children?.length < 2) {
        return true
      }
      if (this.expand_all_child_emails) {
        return true
      }
      if (!index || index == this.selectedEmail?.children?.length - 1) {
        return true
      }
      return false
    }
  }
}
</script>
